/**
 * Add Student State
 *
 * NGXS state for managing student data during the add/create process.
 * This state is stored in local storage to preserve form data across navigation.
 */

import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store, NgxsOnInit } from '@ngxs/store';
import {
  InitializeAddStudent,
  SetStudentField,
  UpdateStudentFields,
  ResetAddStudent,
  SetStudentData,
  ClearAddStudent,
} from './add-student.actions';
import { Student } from 'src/app/models/schema';
import { AuthState } from 'src/app/store/auth';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * Add Student State Model
 */
export interface AddStudentStateModel {
  /** Student ID (optional custom ID) */
  studentId?: string;
  /** Student first name */
  firstName?: string;
  /** Student last name */
  lastName?: string;
  /** Student grade */
  grade?: string;
  /** Date of birth timestamp */
  dateOfBirth?: number;
  /** Student school */
  school?: string;
  /** Student race/ethnicity */
  race?: string;
  /** Student gender */
  gender?: 'Male' | 'Female' | 'Nonbinary';
  /** Disability type */
  disabilityType?: string[];
  /** English learner status */
  englishLearner?: boolean;
  /** Areas of need */
  areasOfNeed?: string[];
  /** Areas of strength */
  areasOfStrength?: string[];
  /** Created at timestamp */
  createdAt?: number;
  /** Created by user ID */
  createdBy?: string;
}

/**
 * Default state
 */
const defaults: AddStudentStateModel = {
  studentId: undefined,
  firstName: undefined,
  lastName: undefined,
  grade: undefined,
  dateOfBirth: undefined,
  school: undefined,
  race: undefined,
  gender: undefined,
  disabilityType: undefined,
  englishLearner: false,
  areasOfNeed: [],
  areasOfStrength: [],
  createdAt: undefined,
  createdBy: undefined,
};

/**
 * Add Student State
 * Stored in local storage with key 'addStudent'
 */
@State<AddStudentStateModel>({
  name: 'addStudent',
  defaults,
})
@Injectable()
export class AddStudentState implements NgxsOnInit {
  constructor(private store: Store) {}

  /**
   * Initialize state listeners
   * 
   * Listens for auth state changes and clears state if user is not authenticated.
   * This handles all cases where user becomes unauthenticated (sign out, account deletion, etc.)
   */
  ngxsOnInit(ctx: StateContext<AddStudentStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes
    this.store.select(AuthState.isAuthenticated).pipe(
      distinctUntilChanged() // Only emit when authentication status actually changes
    ).subscribe((isAuthenticated) => {
      // Only clear if we're transitioning from authenticated to unauthenticated
      // (not on initial load where auth state might not be hydrated yet)
      if (previousAuthState === true && !isAuthenticated) {
        // Reset state
        ctx.setState(defaults);
        
        // Clear the locally stored addStudent from localStorage
        try {
          localStorage.removeItem('@@STATE.addStudent');
          console.log('[AddStudentState] Cleared addStudent from localStorage');
        } catch (error) {
          console.warn('[AddStudentState] Failed to clear addStudent from localStorage:', error);
        }
        
        console.log('[AddStudentState] State cleared - user signed out');
      }

      // Track previous auth state
      previousAuthState = isAuthenticated;
    });
  }

  /**
   * Get the entire add student state
   */
  @Selector()
  static getState(state: AddStudentStateModel): AddStudentStateModel {
    return state;
  }

  /**
   * Get student ID
   */
  @Selector()
  static getStudentId(state: AddStudentStateModel): string | undefined {
    return state.studentId;
  }

  /**
   * Get student first name
   */
  @Selector()
  static getFirstName(state: AddStudentStateModel): string | undefined {
    return state.firstName;
  }

  /**
   * Get student last name
   */
  @Selector()
  static getLastName(state: AddStudentStateModel): string | undefined {
    return state.lastName;
  }

  /**
   * Get student grade
   */
  @Selector()
  static getGrade(state: AddStudentStateModel): string | undefined {
    return state.grade;
  }

  /**
   * Get date of birth
   */
  @Selector()
  static getDateOfBirth(state: AddStudentStateModel): number | undefined {
    return state.dateOfBirth;
  }

  /**
   * Get student school
   */
  @Selector()
  static getSchool(state: AddStudentStateModel): string | undefined {
    return state.school;
  }

  /**
   * Get student race
   */
  @Selector()
  static getRace(state: AddStudentStateModel): string | undefined {
    return state.race;
  }

  /**
   * Get student gender
   */
  @Selector()
  static getGender(state: AddStudentStateModel): 'Male' | 'Female' | 'Nonbinary' | undefined {
    return state.gender;
  }

  /**
   * Get disability type
   */
  @Selector()
  static getDisabilityType(state: AddStudentStateModel): string[] | undefined {
    return state.disabilityType;
  }

  /**
   * Get english learner status
   */
  @Selector()
  static getEnglishLearner(state: AddStudentStateModel): boolean | undefined {
    return state.englishLearner;
  }

  /**
   * Get areas of need
   */
  @Selector()
  static getAreasOfNeed(state: AddStudentStateModel): string[] {
    return state.areasOfNeed || [];
  }

  /**
   * Get areas of strength
   */
  @Selector()
  static getAreasOfStrength(state: AddStudentStateModel): string[] {
    return state.areasOfStrength || [];
  }

  /**
   * Get created at timestamp
   */
  @Selector()
  static getCreatedAt(state: AddStudentStateModel): number | undefined {
    return state.createdAt;
  }

  /**
   * Get created by user ID
   */
  @Selector()
  static getCreatedBy(state: AddStudentStateModel): string | undefined {
    return state.createdBy;
  }

  /**
   * Get student data as Student object
   */
  @Selector()
  static getStudentData(state: AddStudentStateModel): Partial<Student> {
    return {
      studentId: state.studentId,
      firstName: state.firstName,
      lastName: state.lastName,
      grade: state.grade,
      dateOfBirth: state.dateOfBirth,
      school: state.school,
      race: state.race,
      gender: state.gender,
      disabilityType: state.disabilityType,
      englishLearner: state.englishLearner,
      areasOfNeed: state.areasOfNeed,
      areasOfStrength: state.areasOfStrength,
      createdAt: state.createdAt,
      createdBy: state.createdBy,
    };
  }

  /**
   * Check if form has any data
   */
  @Selector()
  static hasData(state: AddStudentStateModel): boolean {
    return !!(
      state.studentId ||
      state.firstName ||
      state.lastName ||
      state.grade ||
      state.dateOfBirth ||
      state.school ||
      state.race ||
      state.gender ||
      state.disabilityType ||
      (state.areasOfNeed && state.areasOfNeed.length > 0) ||
      (state.areasOfStrength && state.areasOfStrength.length > 0)
    );
  }

  /**
   * Initialize add student form
   * Only resets to defaults if there's no existing data
   */
  @Action(InitializeAddStudent)
  initializeAddStudent(ctx: StateContext<AddStudentStateModel>) {
    const currentState = ctx.getState();
    
    // Only initialize to defaults if there's no data in state
    // This preserves data loaded from local storage
    const hasData = !!(
      currentState.studentId ||
      currentState.firstName ||
      currentState.lastName ||
      currentState.grade ||
      currentState.dateOfBirth ||
      currentState.school ||
      currentState.race ||
      currentState.gender ||
      currentState.disabilityType ||
      (currentState.areasOfNeed && currentState.areasOfNeed.length > 0) ||
      (currentState.areasOfStrength && currentState.areasOfStrength.length > 0)
    );

    if (!hasData) {
      ctx.setState(defaults);
    }
    // If there is data, do nothing - preserve the existing state
  }

  /**
   * Set a student field
   */
  @Action(SetStudentField)
  setStudentField(
    ctx: StateContext<AddStudentStateModel>,
    action: SetStudentField
  ) {
    ctx.patchState({
      [action.field]: action.value,
    } as Partial<AddStudentStateModel>);
  }

  /**
   * Update multiple student fields
   */
  @Action(UpdateStudentFields)
  updateStudentFields(
    ctx: StateContext<AddStudentStateModel>,
    action: UpdateStudentFields
  ) {
    ctx.patchState(action.updates as Partial<AddStudentStateModel>);
  }

  /**
   * Reset add student form
   */
  @Action(ResetAddStudent)
  resetAddStudent(ctx: StateContext<AddStudentStateModel>) {
    ctx.setState(defaults);
  }

  /**
   * Set student data (e.g., when editing)
   */
  @Action(SetStudentData)
  setStudentData(
    ctx: StateContext<AddStudentStateModel>,
    action: SetStudentData
  ) {
    ctx.patchState(action.student as Partial<AddStudentStateModel>);
  }

  /**
   * Clear add student form
   */
  @Action(ClearAddStudent)
  clearAddStudent(ctx: StateContext<AddStudentStateModel>) {
    ctx.setState(defaults);
  }
}

